// Printable per-student Exam Report Card.
//
// Same approach as the management MIS report card (ExamReportsTab): the card is built as
// real HTML and printed through a hidden iframe — deliberately NOT an html2canvas raster,
// which mis-renders fixed layouts and produces blurry text on A4.
//
// Everything here is lecturer-scoped: the subject rows, the totals, and the cohort behind
// rank / percentile / appeared all follow the lecturer's authorized subjects and sections,
// never the whole exam.

import * as Cookies from "es-cookie";

const norm = (v) => String(v == null ? "" : v).trim().toLowerCase();
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const sum = (list, key) => list.reduce((acc, x) => acc + num(x[key]), 0);
const r2 = (n) => Math.round(n * 100) / 100;

export const esc = (v) =>
    String(v == null ? "" : v).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

// Marks are Floats (schemes can be +4 / -1, but also 0.5 or 0.25) — trim the trailing
// ".0" rather than printing "180.00" everywhere.
export const fmtMarks = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "—";
    return Number.isInteger(n) ? String(n) : String(r2(n));
};

export const fmtCardDate = (epoch) => {
    if (!epoch) return "—";
    let n = parseInt(epoch, 10);
    if (!n) return "—";
    if (n < 1e12) n *= 1000; // seconds -> ms
    const d = new Date(n);
    return isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
};

// getOrganisationProfile returns the logo as a bare filename ("1782126069892.png") served
// from the files host. Tolerate a full URL or base64 too, in case it is ever stored so.
const LOGO_BASE = "https://support.rizee.in/files/";
export const normalizeLogoSrc = (v) => {
    if (!v) return "";
    const s = String(v).trim();
    if (!s || s === "null" || s === "undefined") return "";
    if (s.startsWith("data:") || /^https?:\/\//i.test(s) || s.startsWith("//")) return s;
    if (/^\/9j\//.test(s)) return `data:image/jpeg;base64,${s}`;
    if (/^iVBORw0KGgo/.test(s)) return `data:image/png;base64,${s}`;
    if (/^R0lGOD/.test(s)) return `data:image/gif;base64,${s}`;
    if (/^[A-Za-z0-9+/]{200,}={0,2}$/.test(s)) return `data:image/jpeg;base64,${s}`;
    return LOGO_BASE + s.replace(/^\/+/, ""); // bare filename
};

const allowedBy = (list) => {
    const allow = (list || []).map(norm).filter(Boolean);
    // An empty authorization list means unrestricted (same convention as authHelper),
    // never "hide everything".
    return (name) => !allow.length || allow.includes(norm(name));
};

// One student's figures reduced to the lecturer's subjects. `subjects` keeps only the
// authorized subjects that were actually part of this paper; marks / correct / wrong /
// questions are re-totalled over exactly those rows so the card's Total line and the
// subject rows can never disagree.
const scopeStudent = (student, isSubjectAllowed) => {
    const paperSubjects = (student.subjects || []).filter((x) => num(x.total) > 0);
    const subjects = paperSubjects.filter((x) => isSubjectAllowed(x.subject));

    // No subject breakdown at all in this exam's response — fall back to the whole-paper
    // figures rather than inventing a per-subject split.
    if (paperSubjects.length === 0) {
        return {
            hasBreakdown: false,
            inScope: true,
            subjects: [],
            marks: num(student.marks),
            maxMarks: num(student.maxMarks),
            correct: num(student.correct),
            wrong: num(student.wrong),
            totalQuestions: num(student.totalQuestions),
        };
    }

    return {
        hasBreakdown: true,
        // The paper has subjects, but none of them is a subject this lecturer teaches —
        // there is nothing in scope to report on for this exam.
        inScope: subjects.length > 0,
        subjects,
        marks: r2(sum(subjects, "marks")),
        maxMarks: r2(sum(subjects, "maxMarks")),
        correct: sum(subjects, "correct"),
        wrong: sum(subjects, "wrong"),
        totalQuestions: sum(subjects, "total"),
    };
};

// Rank + percentile are re-derived WITHIN the lecturer's cohort (their sections) on the
// lecturer's own subject marks — the same rules the backend applies exam-wide: standard
// competition ranking (equal marks share a rank, the next rank then skips) and
// percentile = share of the cohort scoring strictly below.
//
// Returns null when the student is outside the lecturer's scope for this exam.
export const buildScopedReportCard = (examDetail, student, opts = {}) => {
    const { authorizedSectionNames = [], authorizedSubjectNames = [] } = opts;
    if (!examDetail || !student) return null;

    const isSubjectAllowed = allowedBy(authorizedSubjectNames);
    const isSectionAllowed = allowedBy(authorizedSectionNames);

    const self = scopeStudent(student, isSubjectAllowed);
    if (!self.inScope) return null;

    const cohort = (examDetail.students || [])
        .filter((s) => isSectionAllowed(s.sectionName))
        .map((s) => ({ raw: s, scoped: scopeStudent(s, isSubjectAllowed) }))
        .filter((x) => x.scoped.inScope);

    const ranked = [...cohort].sort(
        (a, b) => b.scoped.marks - a.scoped.marks || b.scoped.correct - a.scoped.correct
    );
    const appeared = ranked.length;
    const marksAsc = ranked.map((x) => x.scoped.marks).sort((a, b) => a - b);
    const countBelow = (m) => marksAsc.filter((x) => x < m).length;

    const key = (s) => `${norm(s.name)}|${s.studentUserId == null ? "" : s.studentUserId}|${norm(s.sectionName)}`;
    const isSelf = (s) => s === student || key(s) === key(student);
    let rank = null;
    let curRank = 0;
    let prevMarks = null;
    ranked.forEach((x, i) => {
        if (prevMarks === null || x.scoped.marks !== prevMarks) {
            curRank = i + 1;
            prevMarks = x.scoped.marks;
        }
        if (rank === null && isSelf(x.raw)) rank = curRank;
    });

    // The student sat this exam in a section outside the lecturer's scope — quoting a rank
    // out of a cohort they aren't part of would be wrong, so report nothing at all.
    if (rank === null) return null;

    const unattempted = Math.max(0, self.totalQuestions - self.correct - self.wrong);
    const pct = self.maxMarks ? Math.round((self.marks / self.maxMarks) * 100) : 0;

    return {
        name: student.name,
        mobile: student.mobile,
        sectionName: student.sectionName,
        subjects: self.subjects.map((x) => ({
            subject: x.subject,
            total: num(x.total),
            correct: num(x.correct),
            wrong: num(x.wrong),
            unattempted: Math.max(0, num(x.total) - num(x.correct) - num(x.wrong)),
            marks: num(x.marks),
            maxMarks: num(x.maxMarks),
            pct: num(x.maxMarks) ? Math.round((num(x.marks) / num(x.maxMarks)) * 100) : num(x.pct),
        })),
        hasBreakdown: self.hasBreakdown,
        totalQuestions: self.totalQuestions,
        correct: self.correct,
        wrong: self.wrong,
        unattempted,
        marks: self.marks,
        maxMarks: self.maxMarks,
        pct,
        rank,
        appeared,
        percentile: appeared ? Math.round((countBelow(self.marks) / appeared) * 100) : 0,
        // True when the card describes a slice of the paper rather than the whole exam —
        // drives the footnote so nobody reads a part-paper total as the exam total.
        isSubjectScoped: self.hasBreakdown && self.subjects.length < (student.subjects || []).filter((x) => num(x.total) > 0).length,
    };
};

// Used when the organisation profile hasn't loaded (or isn't set up) — the card still
// prints, just with the institution name from cookies instead of the full letterhead.
export const cookieInstitutionName = () =>
    Cookies.get("institutionname") || Cookies.get("institutename") || Cookies.get("institute_name") || "Institution";

// `card` comes from buildScopedReportCard, `exam` is the getExamResultDetail payload
// (name / className / date / marksPerCorrect / marksPerWrong), `org` the organisation
// profile that carries the logo + address.
export const buildReportCardHTML = (card, exam, org = {}, fallbackSchoolName = cookieInstitutionName()) => {
    const school = (org && org.organisation_name) || fallbackSchoolName || "Institution";
    const logo = normalizeLogoSrc(org && org.logo);
    const address = ((org && org.complete_address) || "").trim();

    const subjectRows = card.subjects
        .map(
            (x) => `
      <tr>
        <td class="l">${esc(x.subject)}</td>
        <td>${x.total}</td>
        <td class="ok">${x.correct}</td>
        <td class="bad">${x.wrong}</td>
        <td>${x.unattempted}</td>
        <td class="b">${fmtMarks(x.marks)} <span class="mut">/ ${fmtMarks(x.maxMarks)}</span></td>
        <td>${x.pct}%</td>
      </tr>`
        )
        .join("");

    const stat = (label, value) =>
        `<div class="stat"><div class="sl">${esc(label)}</div><div class="sv">${esc(value)}</div></div>`;

    const notes = [];
    if (exam.marksPerCorrect) {
        notes.push(
            `Marking scheme: +${fmtMarks(exam.marksPerCorrect)} per correct${exam.marksPerWrong ? `, −${fmtMarks(exam.marksPerWrong)} per wrong` : ""}.`
        );
    }
    notes.push(`Percentile = % of the ${card.appeared} students who scored below this student.`);
    if (card.sectionName) notes.push(`Section ${card.sectionName}.`);
    notes.push(
        card.isSubjectScoped
            ? "Marks, rank and percentile cover only the subjects and sections assigned to you — not the full exam."
            : "Rank and percentile are within the sections assigned to you."
    );

    return `
      <div class="card">
        <div class="hd">
          ${logo ? `<img class="logo" src="${esc(logo)}" alt="" />` : ""}
          <div class="hdtext">
            <div class="school">${esc(school)}</div>
            ${address ? `<div class="addr">${esc(address)}</div>` : ""}
            <div class="title">Exam Report Card</div>
          </div>
        </div>

        <div class="meta">
          <div><span class="k">Student</span><span class="v">${esc(card.name)}</span></div>
          <div><span class="k">Mobile</span><span class="v">${esc(card.mobile || "—")}</span></div>
          <div><span class="k">Exam</span><span class="v">${esc(exam.name)}</span></div>
          <div><span class="k">Class</span><span class="v">${esc(exam.className || "—")}</span></div>
          <div><span class="k">Date</span><span class="v">${esc(fmtCardDate(exam.date))}</span></div>
          <div><span class="k">Appeared</span><span class="v">${card.appeared} students</span></div>
        </div>

        <table class="tbl">
          <thead>
            <tr>
              <th class="l">Subject</th><th>Questions</th><th>Correct</th><th>Wrong</th>
              <th>Unattempted</th><th>Marks</th><th>%</th>
            </tr>
          </thead>
          <tbody>
            ${subjectRows || `<tr><td colspan="7" class="mut c">No subject-wise data for this exam.</td></tr>`}
          </tbody>
          <tfoot>
            <tr>
              <td class="l">Total</td>
              <td>${card.totalQuestions}</td>
              <td class="ok">${card.correct}</td>
              <td class="bad">${card.wrong}</td>
              <td>${card.unattempted}</td>
              <td class="b">${fmtMarks(card.marks)} <span class="mut">/ ${fmtMarks(card.maxMarks)}</span></td>
              <td>${card.pct}%</td>
            </tr>
          </tfoot>
        </table>

        <div class="stats">
          ${stat("Marks", `${fmtMarks(card.marks)} / ${fmtMarks(card.maxMarks)}`)}
          ${stat("Percentage", `${card.pct}%`)}
          ${stat("Rank", `${card.rank} of ${card.appeared}`)}
          ${stat("Percentile", String(card.percentile))}
        </div>

        <div class="note">${esc(notes.join(" "))}</div>
      </div>`;
};

export const REPORT_CARD_PRINT_CSS = `
    @page { size: A4; margin: 12mm; }
    *{box-sizing:border-box;-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important}
    body{margin:0;font-family:Arial,Helvetica,sans-serif;color:#0f172a}
    .card{border:1px solid #e2e8f0;border-radius:10px;padding:22px}
    /* One card per sheet when printing a batch; the last must not add a blank page. */
    .card + .card{page-break-before:always}
    .hd{display:flex;align-items:center;gap:16px;border-bottom:2px solid #7c3aed;padding-bottom:12px;margin-bottom:16px}
    .logo{width:64px;height:64px;object-fit:contain;flex-shrink:0}
    .hdtext{flex:1;min-width:0;text-align:center}
    .school{font-size:20px;font-weight:800;text-transform:uppercase;letter-spacing:.5px}
    .addr{font-size:10.5px;color:#64748b;margin-top:3px;line-height:1.4}
    .title{font-size:13px;font-weight:700;color:#7c3aed;text-transform:uppercase;letter-spacing:1.5px;margin-top:5px}
    .meta{display:grid;grid-template-columns:repeat(3,1fr);gap:8px 18px;margin-bottom:18px}
    .meta .k{display:block;font-size:10px;color:#64748b;font-weight:700;text-transform:uppercase;letter-spacing:.4px}
    .meta .v{display:block;font-size:13px;font-weight:600;margin-top:1px}
    .tbl{width:100%;border-collapse:collapse;margin-bottom:18px}
    .tbl th{font-size:10px;text-transform:uppercase;letter-spacing:.4px;color:#64748b;background:#f8fafc;
            border-top:1px solid #e2e8f0;border-bottom:2px solid #e2e8f0;padding:7px 8px;text-align:center}
    .tbl td{font-size:12px;padding:6px 8px;border-bottom:1px solid #f1f5f9;text-align:center}
    .tbl tfoot td{border-top:2px solid #e2e8f0;border-bottom:none;background:#f8fafc;font-weight:800}
    .tbl thead{display:table-header-group}
    .tbl tr{page-break-inside:avoid}
    .l{text-align:left!important} .c{text-align:center}
    .b{font-weight:800} .mut{color:#94a3b8;font-weight:500}
    .ok{color:#16a34a;font-weight:700} .bad{color:#dc2626}
    .stats{display:grid;grid-template-columns:repeat(4,1fr);gap:10px}
    .stat{border:1px solid #e2e8f0;border-radius:8px;padding:10px 12px;text-align:center}
    .sl{font-size:10px;color:#64748b;font-weight:700;text-transform:uppercase;letter-spacing:.4px}
    .sv{font-size:17px;font-weight:800;color:#7c3aed;margin-top:2px}
    .note{margin-top:14px;font-size:10.5px;color:#94a3b8;line-height:1.5}
`;

// Print arbitrary HTML through a hidden iframe. Waits for images (the logo) first, since
// print() does not wait for them itself.
export const printReportCardHTML = (title, bodyHTML) => {
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
    document.body.appendChild(iframe);
    const cleanup = () =>
        setTimeout(() => {
            try {
                document.body.removeChild(iframe);
            } catch (e) { }
        }, 1000);

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(`<!DOCTYPE html><html><head><title>${esc(title)}</title>
      <style>${REPORT_CARD_PRINT_CSS}</style></head><body>${bodyHTML}</body></html>`);
    doc.close();

    const win = iframe.contentWindow;
    const imgs = Array.from(doc.images || []);
    Promise.all(
        imgs.map((img) => (img.complete ? Promise.resolve() : new Promise((res) => { img.onload = res; img.onerror = res; })))
    ).then(() =>
        setTimeout(() => {
            try {
                win.focus();
                win.print();
            } catch (e) { }
            cleanup();
        }, 200)
    );
};

// Match a dashboard row's student against getExamResultDetail's students[]. The two APIs
// share no id (top-students returns student_id, exam detail returns studentUserId), so
// match on id when both sides have one, then fall back to name (+ section when known).
export const findStudentInExam = (examDetail, row) => {
    const students = (examDetail && examDetail.students) || [];
    const rowId = row.student_id;
    if (rowId != null && String(rowId) !== "") {
        const byId = students.find((s) => s.studentUserId != null && String(s.studentUserId) === String(rowId));
        if (byId) return byId;
    }
    const name = norm(row.student_name);
    const section = norm(row.section_name);
    return (
        students.find((s) => norm(s.name) === name && (!section || norm(s.sectionName) === section)) ||
        students.find((s) => norm(s.name) === name) ||
        null
    );
};
